// Wave 3.51 Phase B — Per-row "Request Edit" kebab menu for packet
// ledger tables. Renders a small kebab dropdown attached to a specific
// ledger entry. The classifier inside this helper decides whether the
// entry is eligible for an edit request (and, if so, which entity_type
// + entity_id to submit), so callers stay thin.
//
// Eligibility matrix (Wave 3.51 Phase B v1):
//   - issue_type === 'purchase_return'    → vendor_purchase_return
//                                            entity_id = e.purchase_return_id
//   - type === 'transfer_out' && !transfer_kind  → packet_transfer
//                                            entity_id = e.id (source ledger id)
//   - type === 'transfer_out' && transfer_kind === 'recut' → packet_recut
//                                            entity_id = e.id
//   - type === 'transfer_out' && transfer_kind === 'break' → packet_send_to_broken
//                                            entity_id = e.id
//
// Renders nothing for ineligible rows or rows outside the T+1 window.

import React, { useMemo, useState } from 'react';
import { MoreVertical, PencilLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/lib/auth';
import { isWithinEditWindowT1 } from '@/lib/editFlow';
import { RequestEditDialog } from '@/components/RequestEditDialog';

function classifyEntry(e) {
  if (!e) return null;
  if (e.issue_type === 'purchase_return') {
    if (!e.purchase_return_id) return null;
    return { entityType: 'vendor_purchase_return', entityId: e.purchase_return_id };
  }
  if (e.type === 'transfer_out') {
    if (e.transfer_kind === 'recut') {
      return { entityType: 'packet_recut', entityId: e.id };
    }
    if (e.transfer_kind === 'break') {
      return { entityType: 'packet_send_to_broken', entityId: e.id };
    }
    if (!e.transfer_kind) {
      return { entityType: 'packet_transfer', entityId: e.id };
    }
  }
  return null;
}

/**
 * @param {object} props
 * @param {object} props.entry       The ledger row (`e`)
 * @param {function} [props.onSuccess]
 */
export function PacketLedgerEditKebab({ entry, onSuccess }) {
  const { isSuperAdmin, isCompanyAdmin } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const cls = useMemo(() => classifyEntry(entry), [entry]);
  const inWindow = useMemo(
    () => isWithinEditWindowT1(entry?.created_at || entry?.timestamp),
    [entry]
  );

  if (!cls) return null;
  if (!isSuperAdmin && !isCompanyAdmin) return null;
  if (!inWindow) return null;

  return (
    <span className="inline-flex items-center align-middle" data-testid={`ledger-edit-kebab-${entry.id}`}>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-slate-500 hover:text-amber-700 hover:bg-amber-50"
            data-testid={`ledger-edit-kebab-trigger-${entry.id}`}
            title="Row actions"
          >
            <MoreVertical size={14} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={(ev) => {
              ev.preventDefault();
              setMenuOpen(false);
              setDialogOpen(true);
            }}
            data-testid={`ledger-edit-kebab-item-${entry.id}`}
          >
            <PencilLine size={12} className="mr-2 text-amber-600" /> Request Edit
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {/* Controlled dialog (no internal trigger button). */}
      <RequestEditDialog
        entityType={cls.entityType}
        entityId={cls.entityId}
        testId={`ledger-edit-dialog-${entry.id}`}
        renderTrigger={false}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={(res) => { setDialogOpen(false); if (onSuccess) onSuccess(res); }}
      />
    </span>
  );
}

export default PacketLedgerEditKebab;
